import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { FaRocket, FaMagic, FaNetworkWired, FaCheckCircle, FaTimes, FaArrowRight, FaArrowLeft, FaLightbulb } from "react-icons/fa";
import "../../styles/nodemented-onboarding-modal.css";

const NodementedOnboardingModal = ({
  isOpen = false,
  onClose,
  onComplete,
  onSkip,
  reactivable = true,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(isOpen);

  const steps = [
    {
      title: t('nodementedOnboardingModal.steps.welcome.title'),
      description: t('nodementedOnboardingModal.steps.welcome.description'),
      icon: <FaRocket size={48} className="onboarding-icon-primary" />,
    },
    {
      title: t('nodementedOnboardingModal.steps.features.title'),
      description: (
        <ul className="onboarding-features-list">
          <li><FaNetworkWired /> {t('nodementedOnboardingModal.steps.features.graph')}</li>
          <li><FaMagic /> {t('nodementedOnboardingModal.steps.features.ai')}</li>
          <li><FaLightbulb /> {t('nodementedOnboardingModal.steps.features.search')}</li>
          <li><FaCheckCircle /> {t('nodementedOnboardingModal.steps.features.collaboration')}</li>
        </ul>
      ),
      icon: <FaMagic size={48} className="onboarding-icon-accent" />,
    },
    {
      title: t('nodementedOnboardingModal.steps.demo.title'),
      description: (
        <div className="onboarding-demo">
          <iframe
            className="tutorial-video"
            src="https://www.youtube.com/embed/Qy92Dn3ln30?autoplay=0&controls=1"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ width: '100%', height: '100%', borderRadius: '12px', boxShadow: '0 4px 24px rgba(59,130,246,0.10)' }}
          ></iframe>
          <p>{t('nodementedOnboardingModal.steps.demo.description')}</p>
        </div>
      ),
      icon: <FaNetworkWired size={48} className="onboarding-icon-primary" />,
    },
    {
      title: t('nodementedOnboardingModal.steps.ready.title'),
      description: t('nodementedOnboardingModal.steps.ready.description'),
      icon: <FaCheckCircle size={48} className="onboarding-icon-success" />,
    },
  ];

  useEffect(() => {
    setVisible(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="nodemented-onboarding-overlay" aria-modal="true" role="dialog">
      <div className={`nodemented-onboarding-modal animate-in step-${step}`}>
        <button className="onboarding-close-btn" aria-label={t('nodementedOnboardingModal.actions.close')} onClick={() => { setVisible(false); onClose && onClose(); }}>
          <FaTimes />
        </button>
        <div className="onboarding-content">
          <div className="onboarding-icon">{steps[step].icon}</div>
          <h2 className="onboarding-title">{steps[step].title}</h2>
          <div className="onboarding-description">{steps[step].description}</div>
        </div>
        <div className="onboarding-progress">
          {steps.map((_, idx) => (
            <button
              key={idx}
              className={`progress-dot${step === idx ? " active" : ""}`}
              aria-label={t('nodementedOnboardingModal.navigation.step', { step: idx + 1 })}
              onClick={() => setStep(idx)}
            />
          ))}
        </div>
        <div className="onboarding-actions">
          {step > 0 ? (
            <button className="onboarding-btn prev" onClick={() => setStep(step - 1)}>
              <FaArrowLeft /> {t('nodementedOnboardingModal.actions.previous')}
            </button>
          ) : (
            <span />
          )}
          {step < steps.length - 1 ? (
            <button className="onboarding-btn next" onClick={() => setStep(step + 1)}>
              {t('nodementedOnboardingModal.actions.next')} <FaArrowRight />
            </button>
          ) : (
            <button
              className="onboarding-btn start pulse"
              onClick={() => {
                setVisible(false);
                onComplete && onComplete();
              }}
            >
              <FaRocket /> {t('nodementedOnboardingModal.actions.start')}
            </button>
          )}
        </div>
        {reactivable && (
          <button className="onboarding-skip-btn" onClick={() => { setVisible(false); onSkip && onSkip(); }}>
            {t('nodementedOnboardingModal.actions.skip')}
          </button>
        )}
      </div>
    </div>
  );
};

export default NodementedOnboardingModal;
